import React, { useState } from 'react';
import { Search as SearchIcon, Send as SendIcon, ArrowLeft, MessageSquare as MessageSquareIcon } from 'lucide-react';
import { conversations, messages, people } from '../../data/mockData';
interface MessagesPageProps {
  tenantId: string;
  onNavigate: (page: string, id?: string) => void;
}
export function MessagesPage({
  tenantId,
  onNavigate
}: MessagesPageProps) {
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(conversations.length > 0 ? conversations[0].id : null);
  const [newMessage, setNewMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const getParticipant = (conv: (typeof conversations)[0]) => {
    const otherId = conv.participants.find(pid => pid !== 'me');
    return people.find(p => p.id === otherId);
  };
  const filteredConversations = conversations.filter(conv => {
    if (!searchTerm) return true;
    const participant = getParticipant(conv);
    if (!participant) return false;
    const fullName = `${participant.firstName} ${participant.lastName}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });
  const selectedConversation = conversations.find(c => c.id === selectedConversationId);
  const selectedParticipant = selectedConversation ? getParticipant(selectedConversation) : null;
  const conversationMessages = selectedConversationId ? messages[selectedConversationId] || [] : [];
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" style={{
    height: 'calc(100vh - 10rem)'
  }} data-id="element-527">
      <div className="flex h-full" data-id="element-528">
        {/* Conversation List */}
        <div className="w-80 border-r border-slate-200 flex flex-col flex-shrink-0" data-id="element-529">
          <div className="p-4 border-b border-slate-100" data-id="element-530">
            <div className="relative" data-id="element-531">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-532" />
              <input type="text" placeholder="Konversation suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none" data-id="element-533" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto" data-id="element-534">
            {filteredConversations.map(conv => {
            const participant = getParticipant(conv);
            if (!participant) return null;
            const isSelected = conv.id === selectedConversationId;
            return <button key={conv.id} onClick={() => setSelectedConversationId(conv.id)} className={`w-full flex items-start gap-3 p-4 text-left transition-colors ${isSelected ? 'bg-emerald-50 border-r-2 border-emerald-500' : 'hover:bg-slate-50'}`} data-id="element-535">
                  <div className="relative flex-shrink-0" data-id="element-536">
                    <div className="w-11 h-11 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600" data-id="element-537">
                      {participant.avatar}
                    </div>
                    {participant.isOnline && <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" data-id="element-538"></div>}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-539">
                    <div className="flex items-center justify-between" data-id="element-540">
                      <h4 className={`text-sm font-bold truncate ${isSelected ? 'text-emerald-700' : 'text-slate-900'}`} data-id="element-541">
                        {participant.firstName} {participant.lastName}
                      </h4>
                      <span className="text-[11px] text-slate-400 flex-shrink-0 ml-2" data-id="element-542">
                        {conv.lastMessage.timestamp}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate mt-0.5" data-id="element-543">
                      {participant.companyName}
                    </p>
                    <p className="text-xs text-slate-500 truncate mt-1" data-id="element-544">
                      {conv.lastMessage.senderId === 'me' && <span className="text-slate-400" data-id="element-545">Sie: </span>}
                      {conv.lastMessage.content}
                    </p>
                  </div>
                  {conv.unreadCount > 0 && <span className="bg-emerald-500 text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-546">
                      {conv.unreadCount}
                    </span>}
                </button>;
          })}
          </div>
        </div>

        {/* Chat Area */}
        {selectedConversation && selectedParticipant ? <div className="flex-1 flex flex-col min-w-0" data-id="element-547">
            {/* Chat Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white" data-id="element-548">
              <div className="flex items-center gap-3" data-id="element-549">
                <div className="relative" data-id="element-550">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-sm font-bold text-slate-600" data-id="element-551">
                    {selectedParticipant.avatar}
                  </div>
                  {selectedParticipant.isOnline && <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full" data-id="element-552"></div>}
                </div>
                <div data-id="element-553">
                  <h3 className="font-bold text-slate-900 text-sm cursor-pointer hover:text-emerald-600 transition-colors" onClick={() => onNavigate('person-profile', selectedParticipant.id)} data-id="element-554">
                    {selectedParticipant.firstName}{' '}
                    {selectedParticipant.lastName}
                  </h3>
                  <p className="text-xs text-slate-500" data-id="element-555">
                    {selectedParticipant.role} •{' '}
                    {selectedParticipant.companyName}
                  </p>
                </div>
              </div>
              <button onClick={() => onNavigate('person-profile', selectedParticipant.id)} className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors" data-id="element-556">
                Profil ansehen
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4 bg-slate-50/50" data-id="element-557">
              {conversationMessages.length > 0 ? conversationMessages.map(msg => {
            const isMe = msg.senderId === 'me';
            return <div key={msg.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`} data-id="element-558">
                      <div className={`max-w-[70%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${isMe ? 'bg-emerald-500 text-white rounded-br-md' : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-sm'}`} data-id="element-559">
                        <p data-id="element-560">{msg.content}</p>
                        <p className={`text-[10px] mt-1.5 ${isMe ? 'text-emerald-200' : 'text-slate-400'}`} data-id="element-561">
                          {msg.timestamp}
                        </p>
                      </div>
                    </div>;
          }) : <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-562">
                  <MessageSquareIcon size={40} className="mb-3 text-slate-300" data-id="element-563" />
                  <p className="text-sm" data-id="element-564">Starten Sie die Konversation.</p>
                </div>}
            </div>

            {/* Message Input */}
            <div className="px-6 py-4 border-t border-slate-200 bg-white" data-id="element-565">
              <div className="flex items-center gap-3" data-id="element-566">
                <input type="text" placeholder="Nachricht schreiben..." value={newMessage} onChange={e => setNewMessage(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter' && newMessage.trim()) {
                setNewMessage('');
              }
            }} className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none" data-id="element-567" />
                <button className={`p-2.5 rounded-xl transition-colors ${newMessage.trim() ? 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-sm' : 'bg-slate-100 text-slate-400 cursor-not-allowed'}`} data-id="element-568">
                  <SendIcon size={18} data-id="element-569" />
                </button>
              </div>
            </div>
          </div> : <div className="flex-1 flex flex-col items-center justify-center text-slate-400 bg-slate-50/50" data-id="element-570">
            <MessageSquareIcon size={48} className="mb-4 text-slate-300" data-id="element-571" />
            <h3 className="text-lg font-bold text-slate-700 mb-1" data-id="element-572">
              Nachrichten
            </h3>
            <p className="text-sm" data-id="element-573">Wählen Sie eine Konversation aus.</p>
          </div>}
      </div>
    </div>;
}